/**
 * The Clipboard_Manager's DOM half (Requirements 12.6, 12.7, 12.8, 12.10).
 *
 * Everything about *what* a copy or a paste means lives in `core/clipboard`. This
 * module owns only the browser's part: getting bytes onto the system clipboard and
 * back off it, across the two APIs a browser may offer.
 *
 * **Two paths, in this order.**
 *
 * 1. `navigator.clipboard` with a `ClipboardItem`, when the page has it and the
 *    permission holds. This is the only way to write several flavours at once
 *    (`application/json`, `text/plain`, `text/html`) outside a paste event, which is
 *    what an internal copy needs in order to survive a round trip with its formulas
 *    and formatting intact.
 * 2. A synthetic `document.execCommand('copy')` over a hidden textarea, when the
 *    async API is unavailable or refuses. Only `text/plain` survives that path, so an
 *    internal copy degrades to values — stated here rather than silently.
 *
 * The internal payload is also cached in memory. That is not an optimization: a
 * headless browser may refuse clipboard permission entirely, and a Firefox paste has
 * no `application/json` flavour at all, so the in-memory copy is what keeps an
 * internal paste lossless in this tab. The system clipboard remains the source of
 * truth when it carries our marker, so a copy in another tab still wins.
 */

import {
  clipToHtml,
  clipToText,
  decodeClipEnvelope,
  encodeClipEnvelope,
  parseDelimitedText,
  payloadFromTable,
  CLIP_JSON_TYPE,
} from '../core/clipboard/index.js';
import type { ClipPayload } from '../core/command/index.js';

/** `\r\n` and `\r` collapsed to `\n`, for comparing clipboard text across platforms. */
function normalizeNewlines(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

/** Where a payload came from, for the notice the shell shows. */
export type ClipSource = 'internal' | 'external' | 'none';

export interface ClipboardRead {
  payload: ClipPayload | null;
  source: ClipSource;
  /** The delimiter an external paste was split on, for the notice. */
  delimiter?: string;
}

/** The browser surface this module needs, narrowed so a test can supply it. */
export interface ClipboardBridge {
  write(payload: ClipPayload): Promise<boolean>;
  read(): Promise<ClipboardRead>;
}

interface ClipboardApi {
  write?: (items: unknown[]) => Promise<void>;
  writeText?: (text: string) => Promise<void>;
  read?: () => Promise<unknown[]>;
  readText?: () => Promise<string>;
}

function clipboardApi(): ClipboardApi | null {
  const nav = typeof navigator === 'undefined' ? null : (navigator as Navigator & { clipboard?: ClipboardApi });
  return nav?.clipboard ?? null;
}

/** Writes `text` through a hidden textarea and `execCommand`. */
function writeTextFallback(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('aria-hidden', 'true');
  area.style.position = 'fixed';
  area.style.left = '-10000px';
  area.style.top = '0';
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    // Deprecated, and the only synchronous write a browser without the async API
    // offers. Guarded because a hostile or headless environment may not have it.
    ok = typeof document.execCommand === 'function' && document.execCommand('copy');
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}

/** The system-clipboard bridge, plus the in-tab payload cache. */
export function createClipboardBridge(): ClipboardBridge {
  let held: ClipPayload | null = null;
  let heldText = '';

  async function write(payload: ClipPayload): Promise<boolean> {
    held = payload;
    const text = clipToText(payload);
    heldText = text;
    const api = clipboardApi();
    const ItemCtor = (globalThis as { ClipboardItem?: new (items: Record<string, Blob>) => unknown })
      .ClipboardItem;
    if (api?.write !== undefined && ItemCtor !== undefined && typeof Blob === 'function') {
      try {
        await api.write([
          new ItemCtor({
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([clipToHtml(payload)], { type: 'text/html' }),
            [CLIP_JSON_TYPE]: new Blob([encodeClipEnvelope(payload)], { type: CLIP_JSON_TYPE }),
          }),
        ]);
        return true;
      } catch {
        // Permission refused or the flavour set rejected: fall through to text.
      }
    }
    if (api?.writeText !== undefined) {
      try {
        await api.writeText(text);
        return true;
      } catch {
        // Fall through to the synchronous path.
      }
    }
    return writeTextFallback(text);
  }

  async function read(): Promise<ClipboardRead> {
    const api = clipboardApi();
    if (api?.read !== undefined) {
      try {
        const items = (await api.read()) as {
          types: readonly string[];
          getType: (type: string) => Promise<Blob>;
        }[];
        for (const item of items) {
          if (item.types.includes(CLIP_JSON_TYPE)) {
            const decoded = decodeClipEnvelope(await (await item.getType(CLIP_JSON_TYPE)).text());
            if (decoded !== null) return { payload: decoded, source: 'internal' };
          }
        }
        for (const item of items) {
          if (item.types.includes('text/plain')) {
            const text = await (await item.getType('text/plain')).text();
            return fromText(text, held, heldText);
          }
        }
      } catch {
        // Permission refused: try plain text, then the in-tab payload.
      }
    }
    if (api?.readText !== undefined) {
      try {
        return fromText(await api.readText(), held, heldText);
      } catch {
        // Fall through to the in-tab payload.
      }
    }
    return held === null ? { payload: null, source: 'none' } : { payload: held, source: 'internal' };
  }

  return { write, read };
}

/**
 * Interprets clipboard text (Requirement 12.8).
 *
 * Our own JSON wins. Text identical to what this tab last copied is answered with the
 * held payload, so an internal copy keeps its formulas and formatting even when the
 * browser only handed back `text/plain`. Anything else is external: the delimiter is
 * sniffed and the fields are distributed across cells.
 */
export function fromText(
  text: string,
  held: ClipPayload | null = null,
  heldText = '',
): ClipboardRead {
  const internal = decodeClipEnvelope(text);
  if (internal !== null) return { payload: internal, source: 'internal' };
  // Compared with line endings normalized: a native clipboard stores CRLF on Windows,
  // and text that came back one terminator different from what we wrote is still the
  // block we hold — losing its formulas over a carriage return would be absurd.
  if (held !== null && normalizeNewlines(text) === normalizeNewlines(heldText)) {
    return { payload: held, source: 'internal' };
  }
  if (text.length === 0) {
    return held === null ? { payload: null, source: 'none' } : { payload: held, source: 'internal' };
  }
  const table = parseDelimitedText(text);
  return { payload: payloadFromTable(table), source: 'external' };
}

/**
 * Reads a `ClipboardEvent`'s data, for the `paste` listener a browser fires when the
 * user presses `Ctrl+V` without the async read permission.
 */
export function fromClipboardEvent(
  event: { clipboardData: { getData: (type: string) => string } | null },
  held: ClipPayload | null = null,
  heldText = '',
): ClipboardRead {
  const data = event.clipboardData;
  if (data === null) return { payload: held, source: held === null ? 'none' : 'internal' };
  const json = data.getData(CLIP_JSON_TYPE);
  const internal = json.length === 0 ? null : decodeClipEnvelope(json);
  if (internal !== null) return { payload: internal, source: 'internal' };
  return fromText(data.getData('text/plain'), held, heldText);
}
